import {
  Box,
  ListItemButton,
  ListItemText,
  SxProps,
  Theme,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { TaskBasicData, TaskState } from "../features/data/patientApiSlice";

import ClinicianBadgeGroup from "./ClinicianBadgeGroup";
import ConcernPillList from "./ConcernPillList";
import FlaggedForSupervisor from "../../assets/supervisor_review.svg?react";
import OpenIcon from "@mui/icons-material/ArrowForwardIos";
import React from "react";
import { TaskTypeIcon } from "./TaskTypeIcon";
import { createStyleSheet } from "../utils/styles";
import { formatDate } from "../utils/dates";
import { formatDistanceToNow } from "date-fns";
import i18n from "../../i18n";
import { theme } from "../../theme";
import { useCallback } from "react";

const MAX_BADGES = 3;

export const getDisplayDateFromBasicTask = (basicTask: TaskBasicData) => {
  return (
    formatDistanceToNow(basicTask.created, { addSuffix: true }) +
    (basicTask.due_date
      ? ` | ${i18n.t("Patients:dueDate", {
          dueDate: formatDate(new Date(basicTask.due_date!)),
        })}`
      : "")
  );
};

const TaskListItem = (props: {
  id?: string;
  basicTask: TaskBasicData;
  onClick: (patientUid: string, taskUid: string) => void;
  selected: boolean;
}) => {
  const { id, basicTask, onClick, selected } = props;
  const theme = useTheme();

  const handleClick = useCallback(
    () => onClick(basicTask.patient.uid, basicTask.uid),
    [onClick, basicTask.patient.uid, basicTask.uid]
  );

  const isTaskTodo = basicTask.state === TaskState.todo;
  return (
    <ListItemButton
      id={id}
      onClick={handleClick}
      sx={{
        borderRadius: selected ? 2 : 0,
        border: (theme) =>
          "1px solid " +
          (selected ? alpha(theme.palette.primary.main, 0.3) : "transparent"),
        borderBottom: (theme) =>
          !selected ? `1px solid ${alpha(theme.palette.grey[500], 0.3)}` : "",
        backgroundColor: (theme) =>
          selected ? alpha(theme.palette.primary.main, 0.1) : "",
        position: "relative",
        [theme.breakpoints.down("desktop")]: {
          padding: 0.5,
        },
      }}
    >
      <TaskTypeIcon
        type={basicTask.type}
        state={basicTask.state}
        sx={styles.taskIcon}
      />
      <ListItemText
        primary={
          <Box sx={styles.titleContainer}>
            <Typography
              variant="h6"
              noWrap
              color={isTaskTodo ? "primary" : "textSecondary"}
            >
              {basicTask.patient.name}
            </Typography>
            {basicTask.patient.pending_hcw_message ? (
              <Box sx={styles.newMessageDot} />
            ) : null}
            <ConcernPillList
              tasks={[basicTask]}
              overlap
              singlePillHasText
              sx={{ marginLeft: 1 }}
            />
          </Box>
        }
        secondary={getDisplayDateFromBasicTask(basicTask)}
        slotProps={{
          secondary: { noWrap: true, sx: styles.secondaryText },
        }}
      />
      <ClinicianBadgeGroup
        clinicians={basicTask.patient.clinicians_working_in_record || []}
        maxBadges={MAX_BADGES}
      />
      <OpenIcon
        sx={
          [
            styles.openIcon,
            selected ? styles.iconSelected : styles.icon,
          ] as SxProps<Theme>
        }
        fontSize="small"
      />
      {basicTask.flagged_for_supervisor ? (
        <Box sx={styles.flaggedForSupervisor}>
          <FlaggedForSupervisor fill={theme.palette.flagForSupervisor.main} />
        </Box>
      ) : null}
    </ListItemButton>
  );
};

const styles = createStyleSheet({
  secondaryText: {
    display: "inline",
  },
  taskIcon: {
    marginRight: 1,
    [theme.breakpoints.down("desktop")]: {
      marginRight: 0.5,
    },
  },
  titleContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  newMessageDot: {
    backgroundColor: (theme) => theme.palette.newMessageDot.main,
    marginLeft: 1,
    width: 10,
    height: 10,
    [theme.breakpoints.down("desktop")]: {
      marginLeft: 0.75,
      width: 8,
      height: 8,
    },
    borderRadius: "50%",
  },
  openIcon: {
    marginLeft: 1,
    [theme.breakpoints.down("desktop")]: {
      marginLeft: 0.75,
    },
  },
  icon: {
    color: (theme) => theme.palette.text.secondary,
  },
  iconSelected: {
    color: (theme) => theme.palette.primary.main,
  },
  flaggedForSupervisor: {
    position: "absolute",
    top: 0,
    right: 0,
    pointerEvents: "none",
  },
});

export default TaskListItem;
